// Demo fixtures for the public homepage tracker.
//
// These fixtures let the homepage answer the demo AWBs locally — no network, instant — so the "Try"
// chips always render a full, realistic Cargo-IMP timeline even when the airline behind a demo
// number has long since purged it. They are hardcoded, illustrative shipments (not live data); real
// AWBs go to the free public tier, GET /track/public/:awb (no API key).
import type { AwbTrackingData } from '@aircargomcp/types'

export interface Sample {
  awb: string // canonical hyphenated AWB, as shown on the chip
  label: string // short description under/next to the chip
  data: AwbTrackingData
}

// Match on digits only so "203-12345675", "20312345675" and "203 1234 5675" all resolve.
export const sampleKey = (awb: string): string => awb.replace(/\D/g, '')

export const SAMPLES: Sample[] = [
  {
    awb: '203-12345675',
    label: 'delivered Cebu Pacific shipment · full timeline',
    data: {
      awb: '203-12345675',
      airline_prefix: '203',
      airline_iata: '5J',
      airline_name: 'Cebu Pacific Air',
      status: 'delivered',
      origin: 'MNL',
      origin_name: 'Manila Ninoy Aquino Intl',
      destination: 'SIN',
      destination_name: 'Singapore Changi',
      pieces: 12,
      weight_kg: 480,
      flight_number: '5J803',
      eta: '2026-07-28T14:05:00+08:00',
      eta_is_actual: true,
      events: [
        {
          status_code: 'RCS',
          event_family: 'ACCEPTANCE',
          is_actual: true,
          datetime: '2026-07-27T09:12:00+08:00',
          station: 'MNL',
          station_name: 'Manila',
          pieces: 12,
          weight_kg: 480,
          description: 'Received from shipper',
        },
        {
          status_code: 'MAN',
          event_family: 'ACCEPTANCE',
          is_actual: true,
          datetime: '2026-07-27T18:40:00+08:00',
          station: 'MNL',
          station_name: 'Manila',
          flight_number: '5J803',
          description: 'Manifested on flight',
        },
        {
          status_code: 'DEP',
          event_family: 'FLIGHT',
          is_actual: true,
          datetime: '2026-07-28T08:35:00+08:00',
          station: 'MNL',
          station_name: 'Manila',
          flight_number: '5J803',
          pieces: 12,
          weight_kg: 480,
          description: 'Departed on flight',
        },
        {
          status_code: 'ARR',
          event_family: 'FLIGHT',
          is_actual: true,
          datetime: '2026-07-28T12:20:00+08:00',
          station: 'SIN',
          station_name: 'Singapore Changi',
          flight_number: '5J803',
          description: 'Arrived at destination',
        },
        {
          status_code: 'RCF',
          event_family: 'FLIGHT',
          is_actual: true,
          datetime: '2026-07-28T13:05:00+08:00',
          station: 'SIN',
          station_name: 'Singapore Changi',
          pieces: 12,
          weight_kg: 480,
          description: 'Received from flight',
        },
        {
          status_code: 'NFD',
          event_family: 'DELIVERY',
          is_actual: true,
          datetime: '2026-07-28T13:40:00+08:00',
          station: 'SIN',
          station_name: 'Singapore Changi',
          description: 'Consignee notified, ready for pickup',
        },
        {
          status_code: 'DLV',
          event_family: 'DELIVERY',
          is_actual: true,
          datetime: '2026-07-28T14:05:00+08:00',
          station: 'SIN',
          station_name: 'Singapore Changi',
          pieces: 12,
          weight_kg: 480,
          description: 'Delivered to consignee',
        },
      ],
    },
  },
  {
    awb: '176-55503921',
    label: 'in-transit Emirates SkyCargo · HKG → DXB',
    data: {
      awb: '176-55503921',
      airline_prefix: '176',
      airline_iata: 'EK',
      airline_name: 'Emirates SkyCargo',
      status: 'in_transit',
      origin: 'HKG',
      origin_name: 'Hong Kong Intl',
      destination: 'DXB',
      destination_name: 'Dubai Intl',
      pieces: 30,
      weight_kg: 1240,
      flight_number: 'EK9812',
      eta: '2026-08-12T02:15:00+04:00',
      eta_is_actual: false,
      events: [
        {
          status_code: 'RCS',
          event_family: 'ACCEPTANCE',
          is_actual: true,
          datetime: '2026-08-10T21:30:00+08:00',
          station: 'HKG',
          station_name: 'Hong Kong',
          pieces: 30,
          weight_kg: 1240,
          description: 'Received from shipper',
        },
        {
          status_code: 'MAN',
          event_family: 'ACCEPTANCE',
          is_actual: true,
          datetime: '2026-08-11T04:10:00+08:00',
          station: 'HKG',
          station_name: 'Hong Kong',
          flight_number: 'EK9812',
          description: 'Manifested on flight',
        },
        {
          status_code: 'DEP',
          event_family: 'FLIGHT',
          is_actual: true,
          datetime: '2026-08-11T07:55:00+08:00',
          station: 'HKG',
          station_name: 'Hong Kong',
          flight_number: 'EK9812',
          pieces: 30,
          weight_kg: 1240,
          description: 'Departed on flight',
        },
        {
          status_code: 'ARR',
          event_family: 'FLIGHT',
          is_actual: false,
          datetime: '2026-08-12T02:15:00+04:00',
          station: 'DXB',
          station_name: 'Dubai',
          flight_number: 'EK9812',
          description: 'Estimated arrival at destination',
        },
      ],
    },
  },
  {
    awb: '020-48817364',
    label: 'ready for pickup Lufthansa Cargo · FRA → ORD',
    data: {
      awb: '020-48817364',
      airline_prefix: '020',
      airline_iata: 'LH',
      airline_name: 'Lufthansa Cargo',
      status: 'available',
      origin: 'FRA',
      origin_name: 'Frankfurt am Main',
      destination: 'ORD',
      destination_name: 'Chicago O’Hare',
      pieces: 8,
      weight_kg: 315,
      flight_number: 'LH8228',
      eta: '2026-08-09T15:50:00-05:00',
      eta_is_actual: true,
      events: [
        {
          status_code: 'RCS',
          event_family: 'ACCEPTANCE',
          is_actual: true,
          datetime: '2026-08-08T16:05:00+02:00',
          station: 'FRA',
          station_name: 'Frankfurt',
          pieces: 8,
          weight_kg: 315,
          description: 'Received from shipper',
        },
        {
          status_code: 'DEP',
          event_family: 'FLIGHT',
          is_actual: true,
          datetime: '2026-08-09T10:20:00+02:00',
          station: 'FRA',
          station_name: 'Frankfurt',
          flight_number: 'LH8228',
          pieces: 8,
          weight_kg: 315,
          description: 'Departed on flight',
        },
        {
          status_code: 'ARR',
          event_family: 'FLIGHT',
          is_actual: true,
          datetime: '2026-08-09T14:35:00-05:00',
          station: 'ORD',
          station_name: 'Chicago O’Hare',
          flight_number: 'LH8228',
          description: 'Arrived at destination',
        },
        {
          status_code: 'RCF',
          event_family: 'FLIGHT',
          is_actual: true,
          datetime: '2026-08-09T15:20:00-05:00',
          station: 'ORD',
          station_name: 'Chicago O’Hare',
          pieces: 8,
          weight_kg: 315,
          description: 'Received from flight',
        },
        {
          status_code: 'NFD',
          event_family: 'DELIVERY',
          is_actual: true,
          datetime: '2026-08-09T15:50:00-05:00',
          station: 'ORD',
          station_name: 'Chicago O’Hare',
          description: 'Consignee notified, ready for pickup',
        },
      ],
    },
  },
]

const SAMPLE_BY_KEY = new Map(SAMPLES.map((s) => [sampleKey(s.awb), s.data]))

/** Return hardcoded demo data for a sample AWB, or null for anything else (→ live API). */
export function lookupSample(awb: string): AwbTrackingData | null {
  return SAMPLE_BY_KEY.get(sampleKey(awb)) ?? null
}
